import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  ArrowLeft, DollarSign, Search, Plus, Tag, MapPin, Users, Eye, CheckCircle, Building2
} from 'lucide-react';
import { toast } from 'sonner';
import InvestmentDetailView from './InvestmentDetailView';
import LanguageSwitcher from './LanguageSwitcher';

const API = BACKEND_URL;

const INSTRUMENT_LABELS = {
  direct_grant: 'Grant / Subsidy',
  social_bond: 'Social Bond',
  equity: 'Equity',
  loan: 'Loan',
  guarantee: 'Guarantee',
  other: 'Other',
};

const MATURITY_BADGE = {
  early_idea: { label: 'Early Idea', color: 'bg-red-100 text-red-700' },
  developing: { label: 'Developing', color: 'bg-amber-100 text-amber-700' },
  mature: { label: 'Mature', color: 'bg-blue-100 text-blue-700' },
  ready: { label: 'Ready', color: 'bg-green-100 text-green-700' },
};

const MATURITY_RING_COLORS = {
  early_idea: '#B91C1C',   // red-700: 6.04:1 vs white (AA pass)
  developing: '#B45309',   // amber-700: 4.85:1 vs white (AA pass)
  mature: '#1D4ED8',       // blue-700: 6.73:1 vs white (AA pass, was blue-500 = 3.68:1 fail)
  ready: '#047857',        // emerald-700: 4.96:1 vs white (AA pass)
};

// Readiness Score Ring Component
const ReadinessRing = ({ score, maturityLevel, size = 52 }) => {
  const radius = (size - 6) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = Math.min((score || 0) / 5, 1);
  const offset = circumference * (1 - pct);
  const color = MATURITY_RING_COLORS[maturityLevel] || '#374151';  // gray-700: 10.3:1 vs white
  const label = score ? score.toFixed(1) : '—';

  return (
    <div className="relative" style={{ width: size, height: size }} data-testid="readiness-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#E5E7EB" strokeWidth="4" />
        <circle
          cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke={color} strokeWidth="4" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.5s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xs font-bold" style={{ color, lineHeight: 1 }}>{label}</span>
        <span className="text-xs text-gray-700" style={{ lineHeight: 1 }}>/5</span>
      </div>
    </div>
  );
};

const SocialInvestmentRegistration = ({ user }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [investments, setInvestments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [myOnly, setMyOnly] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedInvestment, setSelectedInvestment] = useState(null);
  const [newInv, setNewInv] = useState({
    title: '', description: '', investment_type: 'grant', financial_instrument: 'direct_grant',
    sector: '', beneficiaries: '', estimated_budget: '', location: '', social_actor_id: '',
  });
  // Social Actor picker — list of registered actors used to attribute an investment
  // to the organisation that proposed/owns it. Loaded once on mount.
  const [socialActors, setSocialActors] = useState([]);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchInvestments(); }, [statusFilter, typeFilter, myOnly]);

  useEffect(() => {
    // Fetch the actor list once. Used to power the picker in the create dialog
    // and to render an "Owner organisation" badge on each investment card.
    (async () => {
      try {
        const res = await axios.get(`${API}/api/social-actors`, { headers });
        const list = Array.isArray(res.data) ? res.data : (res.data.actors || []);
        setSocialActors(list);
      } catch {
        setSocialActors([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // For social-actor users, locate the actor record owned by this user so we
  // can pre-select and lock the "Owner organisation" picker in the dialog.
  const myActor = (user?.role === 'social_actor')
    ? socialActors.find((a) => a.user_id === user.id)
    : null;
  const lockOwnerOrg = !!myActor; // social_actor with a profile → locked
  useEffect(() => {
    if (showCreateDialog && lockOwnerOrg && newInv.social_actor_id !== myActor.id) {
      setNewInv((prev) => ({ ...prev, social_actor_id: myActor.id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateDialog, lockOwnerOrg, myActor?.id]);

  // Deep-link: when ?open=<investment_id> is present, auto-open that investment.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const openId = params.get('open');
    if (openId) {
      handleViewInvestment(openId);
    }
  }, []);

  const fetchInvestments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('investment_type', typeFilter);
      if (myOnly) params.append('my_only', 'true');
      const res = await axios.get(`${API}/api/social-investments?${params}`, { headers });
      setInvestments(res.data.investments || []);
      setStats(res.data.stats || {});
    } catch {
      setInvestments([]);
      setStats({ total: 0, by_status: {}, by_type: {} });
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    try {
      await axios.post(`${API}/api/social-investments/seed`, {}, { headers });
      toast.success('Sample data seeded');
      fetchInvestments();
    } catch {
      toast.error('Failed to seed data');
    }
  };

  const handleCreate = async () => {
    if (!newInv.title) { toast.error('Title is required'); return; }
    try {
      const res = await axios.post(`${API}/api/social-investments`, {
        title: newInv.title,
        description: newInv.description,
        investment_type: newInv.investment_type,
        financial_instrument: newInv.financial_instrument,
        sector: newInv.sector,
        estimated_budget: newInv.estimated_budget ? parseFloat(newInv.estimated_budget) : 0,
        location: newInv.location,
        beneficiaries: newInv.beneficiaries ? newInv.beneficiaries.split(',').map(b => b.trim()).filter(Boolean) : [],
        social_actor_id: newInv.social_actor_id || null,
      }, { headers });
      toast.success('Investment created');
      setShowCreateDialog(false);
      setNewInv({ title: '', description: '', investment_type: 'grant', financial_instrument: 'direct_grant', sector: '', beneficiaries: '', estimated_budget: '', location: '', social_actor_id: '' });
      // Open the new investment in detail view
      setSelectedInvestment(res.data);
    } catch {
      toast.error('Failed to create investment');
    }
  };

  const handleViewInvestment = async (invId) => {
    try {
      const res = await axios.get(`${API}/api/social-investments/${invId}`, { headers });
      setSelectedInvestment(res.data);
    } catch {
      toast.error('Failed to load investment');
    }
  };

  const filtered = investments.filter(i => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return i.title?.toLowerCase().includes(s) || i.description?.toLowerCase().includes(s) || i.sector?.toLowerCase().includes(s);
  });

  const formatCurrency = (amount) => {
    if (!amount) return '';
    return `EUR ${new Intl.NumberFormat('el-GR', { maximumFractionDigits: 0 }).format(amount)}`;
  };

  const total = stats?.total || 0;
  const drafts = stats?.by_status?.draft || 0;
  const inDev = stats?.by_status?.in_development || 0;
  const activeCompleted = (stats?.by_status?.active || 0) + (stats?.by_status?.completed || 0);

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div></div>;
  }

  // ========== DETAIL VIEW ==========
  if (selectedInvestment) {
    return (
      <InvestmentDetailView
        investment={selectedInvestment}
        user={user}
        onBack={() => { setSelectedInvestment(null); fetchInvestments(); }}
        onUpdated={(updated) => setSelectedInvestment(updated)}
      />
    );
  }

  // ========== LIST VIEW ==========
  return (
    <div className="space-y-6 p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> {t('social_investment.dashboard', 'Dashboard')}
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <DollarSign className="h-7 w-7 text-emerald-700" /> {t('social_investment.title', 'Social Investment Registry')}
            </h1>
            <p className="text-gray-600 mt-1">{t('social_investment.subtitle', 'Register, track and manage social investment ideas')}</p>
            {user?.role === 'investor' && (
              <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 mt-3 inline-flex items-center gap-1.5">
                <CheckCircle className="h-3.5 w-3.5" />
                {t('social_investment.investor_notice', 'When you propose an investment it is automatically engaged in Participatory Planning, Combined Investments and Supply & Demand Matching.')}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <Button onClick={() => setShowCreateDialog(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="new-investment-btn">
            <Plus className="h-4 w-4 mr-2" /> {t('social_investment.new_investment', 'New Investment')}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-blue-300 bg-blue-50/30"><CardContent className="p-4 text-center"><span className="block text-3xl font-bold text-blue-800">{total}</span><p className="text-sm text-gray-700">{t('social_investment.stat_total', 'Total Investments')}</p></CardContent></Card>
        <Card className="border-amber-300 bg-amber-50/30"><CardContent className="p-4 text-center"><span className="block text-3xl font-bold text-amber-800">{drafts}</span><p className="text-sm text-gray-700">{t('social_investment.stat_drafts', 'Ideas / Drafts')}</p></CardContent></Card>
        <Card className="border-green-300 bg-green-50/30"><CardContent className="p-4 text-center"><span className="block text-3xl font-bold text-green-800">{inDev}</span><p className="text-sm text-gray-700">{t('social_investment.stat_in_development', 'In Development')}</p></CardContent></Card>
        <Card className="border-emerald-300 bg-emerald-50/30"><CardContent className="p-4 text-center"><span className="block text-3xl font-bold text-emerald-800">{activeCompleted}</span><p className="text-sm text-gray-700">{t('social_investment.stat_active_completed', 'Active / Completed')}</p></CardContent></Card>
      </div>

      {/* Search & Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" aria-hidden="true" />
              <label htmlFor="sir-investments-search" className="sr-only">
                {t('social_investment.search_placeholder', 'Search investments...')}
              </label>
              <Input id="sir-investments-search" data-testid="search-investments" placeholder={t('social_investment.search_placeholder', 'Search investments...')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36" data-testid="status-filter" aria-label={t('social_investment.all_statuses', 'All Statuses')}><SelectValue placeholder={t('social_investment.all_statuses', 'All Statuses')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('social_investment.all_statuses', 'All Statuses')}</SelectItem>
                <SelectItem value="draft">{t('social_investment.status_draft', 'Draft')}</SelectItem>
                <SelectItem value="in_development">{t('social_investment.status_in_development', 'In Development')}</SelectItem>
                <SelectItem value="active">{t('social_investment.status_active', 'Active')}</SelectItem>
                <SelectItem value="completed">{t('social_investment.status_completed', 'Completed')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-32" data-testid="type-filter" aria-label={t('social_investment.all_types', 'All Types')}><SelectValue placeholder={t('social_investment.all_types', 'All Types')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('social_investment.all_types', 'All Types')}</SelectItem>
                <SelectItem value="grant">{t('social_investment.type_grant', 'Grant')}</SelectItem>
                <SelectItem value="bond">{t('social_investment.type_bond', 'Bond')}</SelectItem>
                <SelectItem value="equity">{t('social_investment.type_equity', 'Equity')}</SelectItem>
                <SelectItem value="loan">{t('social_investment.type_loan', 'Loan')}</SelectItem>
              </SelectContent>
            </Select>
            <Button variant={myOnly ? 'default' : 'outline'} size="sm" onClick={() => setMyOnly(!myOnly)} data-testid="my-investments-btn" className={myOnly ? 'bg-gray-900' : ''}>
              {t('social_investment.my_investments', 'My Investments')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Investment List */}
      {filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <DollarSign className="h-12 w-12 text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">{t('social_investment.empty_title', 'No Investments Found')}</h3>
            <p className="text-gray-500 text-center max-w-md mb-4">{t('social_investment.empty_subtitle', 'Register your first social investment idea to get started.')}</p>
            <div className="flex gap-3">
              <Button onClick={() => setShowCreateDialog(true)} className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-2" /> {t('social_investment.new_investment', 'New Investment')}</Button>
              {(user?.role === 'administrator' || user?.role === 'project_manager') && (
                <Button onClick={handleSeed} variant="outline" data-testid="seed-sir-btn">{t('social_investment.load_sample_data', 'Load Sample Data')}</Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((inv) => {
            const maturity = inv.self_assessment?.maturity_level;
            const mb = maturity ? MATURITY_BADGE[maturity] : null;
            const hasMedia = (inv.photos || []).length > 0 || (inv.video_urls || []).length > 0 || (inv.attachments || []).length > 0;
            const hasOutcomes = (inv.expected_outcomes || []).length > 0;
            return (
              <Card key={inv.id} data-testid={`investment-${inv.id}`} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleViewInvestment(inv.id)}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    {/* Readiness Ring */}
                    <div className="shrink-0 pt-1">
                      <ReadinessRing
                        score={inv.self_assessment?.total_score}
                        maturityLevel={inv.self_assessment?.maturity_level}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="text-xs capitalize">{inv.status?.replace('_', ' ')}</Badge>
                          <Badge variant="secondary" className="text-xs">{INSTRUMENT_LABELS[inv.financial_instrument] || inv.financial_instrument}</Badge>
                          {inv.participatory_design_enabled && <Badge className="text-xs bg-purple-100 text-purple-700">Participatory</Badge>}
                          {mb && <Badge className={`text-xs ${mb.color}`}>{mb.label}</Badge>}
                          {hasMedia && <Badge className="text-xs bg-pink-100 text-pink-700">Media</Badge>}
                          {hasOutcomes && <Badge className="text-xs bg-green-100 text-green-700">Outcomes</Badge>}
                          {(inv.sdg_alignment || []).length > 0 && <Badge className="text-xs bg-teal-100 text-teal-700">SDG {inv.sdg_alignment.join(', ')}</Badge>}
                        </div>
                        <Eye className="h-5 w-5 text-gray-500 shrink-0" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{inv.title}</h3>
                      <p className="text-sm text-gray-500 mb-3 line-clamp-2">{inv.description}</p>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                        {inv.estimated_budget > 0 && <span className="flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" /> {formatCurrency(inv.estimated_budget)}</span>}
                        {inv.sector && <span className="flex items-center gap-1"><Tag className="h-3.5 w-3.5" /> {inv.sector}</span>}
                        {inv.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {inv.location}</span>}
                        {(inv.target_groups || []).length > 0 && <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {inv.target_groups.map(tg => tg.name).join(', ')}</span>}
                        {(inv.target_groups || []).length === 0 && inv.beneficiaries?.length > 0 && <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {inv.beneficiaries.join(', ')}</span>}
                        {(inv.owner_org_name || inv.social_actor_name) && (
                          <span className="flex items-center gap-1 text-emerald-700" title="Owner organisation">
                            <Building2 className="h-3.5 w-3.5" /> {inv.owner_org_name || inv.social_actor_name}
                          </span>
                        )}
                        <span className="text-xs">by {inv.creator_username} · {new Date(inv.created_at).toLocaleDateString('en-GB')}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{t('social_investment.dialog_new_title', 'New Social Investment')}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div><Label>{t('social_investment.field_title', 'Title')} *</Label><Input value={newInv.title} onChange={(e) => setNewInv({ ...newInv, title: e.target.value })} placeholder={t('social_investment.field_title_placeholder', 'e.g., Digital Inclusion for Rural Elders')} data-testid="inv-title-input" /></div>
            <div><Label>{t('social_investment.field_description', 'Description')}</Label><Textarea value={newInv.description} onChange={(e) => setNewInv({ ...newInv, description: e.target.value })} placeholder={t('social_investment.field_description_placeholder', 'Briefly describe the investment idea...')} rows={3} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('social_investment.field_investment_type', 'Investment Type')}</Label>
                <Select value={newInv.investment_type} onValueChange={v => setNewInv({ ...newInv, investment_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="grant">{t('social_investment.type_grant', 'Grant')}</SelectItem>
                    <SelectItem value="bond">{t('social_investment.type_bond', 'Bond')}</SelectItem>
                    <SelectItem value="equity">{t('social_investment.type_equity', 'Equity')}</SelectItem>
                    <SelectItem value="loan">{t('social_investment.type_loan', 'Loan')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('social_investment.field_financial_instrument', 'Financing Instrument')}</Label>
                <Select value={newInv.financial_instrument} onValueChange={v => setNewInv({ ...newInv, financial_instrument: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[{ value: 'direct_grant', label: t('social_investment.fi_direct_grant', 'Grant / Subsidy') }, { value: 'social_bond', label: t('social_investment.fi_social_bond', 'Social Bond') }, { value: 'equity', label: t('social_investment.fi_equity', 'Equity') }, { value: 'loan', label: t('social_investment.fi_loan', 'Loan') }].map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{t('social_investment.field_sector', 'Sector')}</Label><Input value={newInv.sector} onChange={(e) => setNewInv({ ...newInv, sector: e.target.value })} placeholder={t('social_investment.field_sector_placeholder', 'e.g., Digital Inclusion')} /></div>
              <div><Label>{t('social_investment.field_budget', 'Budget (EUR)')}</Label><Input type="number" value={newInv.estimated_budget} onChange={(e) => setNewInv({ ...newInv, estimated_budget: e.target.value })} placeholder="50000" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{t('social_investment.field_location', 'Location')}</Label><Input value={newInv.location} onChange={(e) => setNewInv({ ...newInv, location: e.target.value })} placeholder={t('social_investment.field_location_placeholder', 'Thessaly, Greece')} /></div>
              <div><Label>{t('social_investment.field_beneficiaries', 'Beneficiaries (comma-separated)')}</Label><Input value={newInv.beneficiaries} onChange={(e) => setNewInv({ ...newInv, beneficiaries: e.target.value })} placeholder={t('social_investment.field_beneficiaries_placeholder', 'Elderly, Rural Communities')} /></div>
            </div>
            <div>
              <Label>{t('social_investment.field_owner_org', 'Owner organisation (Social Actor)')}</Label>
              {lockOwnerOrg ? (
                <div
                  className="w-full mt-1 flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-sm"
                  data-testid="inv-owner-org-locked"
                >
                  <span className="font-medium text-slate-800 truncate">
                    {myActor.organization_name}
                    {myActor.city ? <span className="text-slate-500"> · {myActor.city}</span> : null}
                  </span>
                  <span className="text-xs uppercase tracking-wide text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">
                    {t('social_investment.field_owner_org_locked', 'your organisation')}
                  </span>
                </div>
              ) : (
                <Select
                  value={newInv.social_actor_id || 'none'}
                  onValueChange={(v) => setNewInv({ ...newInv, social_actor_id: v === 'none' ? '' : v })}
                >
                  <SelectTrigger data-testid="inv-social-actor-select"><SelectValue placeholder={t('social_investment.field_owner_org_placeholder', 'Select the registered organisation that owns this investment')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('social_investment.field_owner_org_none', '— Not linked yet —')}</SelectItem>
                    {socialActors.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.organization_name}{a.city ? ` · ${a.city}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {lockOwnerOrg
                  ? t('social_investment.field_owner_org_helper_locked', 'New investments you create are automatically attributed to your organisation. Only administrators or project managers can change this.')
                  : t('social_investment.field_owner_org_helper', 'Linking this investment to a Social Actor enables reverse lookup and matching with investors.')}
              </p>
            </div>
            <p className="text-xs text-gray-500">{t('social_investment.dialog_helper', 'After creating, you will be taken to the detailed form to add beneficiaries, financial plan, media, and run the self-assessment.')}</p>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>{t('common.cancel', 'Cancel')}</Button>
              <Button onClick={handleCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-inv-btn">{t('social_investment.dialog_submit', 'Create & Continue')}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SocialInvestmentRegistration;
